// Pré-contrôle en lecture seule, avant toute écriture. Rejoue les contrôles de
// care qui dépendent des dates d'un remplacement (care_v1.2
// packages/affectations/src/validation.js, appelé par `checkAffectation` à
// chaque création ou modification, et par `createBundle`) :
// - verificationChevauchement → `findOverlaps` ;
// - checkCleanerDates et checkContractDates → `checkDates`.
// Une donnée absente (champ masqué par les droits du rôle) saute le contrôle
// concerné : care reste juge en dernier ressort.

import { WEEKDAYS, formatDay } from './dates';

export const OVERLAP_TYPES = [
  'Permanent',
  'Ponctuel',
  'Remplacement',
  'Remplacement Saisonnier',
];

const hhmm = (time) => {
  return String(time || '').substring(0, 5);
};

export const timesOverlap = (start, end, otherStart, otherEnd) => {
  return hhmm(start) < hhmm(otherEnd) && hhmm(end) > hhmm(otherStart);
};

const has = (object, key) => {
  return !!object && Object.prototype.hasOwnProperty.call(object, key);
};

const day = (date) => {
  return date ? String(date).slice(0, 10) : null;
};

/**
 * Affectations du remplaçant que care jugerait en chevauchement avec une
 * affectation posée sur `range` avec ces créneaux : même règle que
 * verificationChevauchement (types ci-dessus, dates qui se recouvrent, un jour
 * commun, horaires qui se recouvrent). `others` exclut déjà le remplacement
 * lui-même (édition).
 *
 * @param {Object} range { start_date, end_date }
 * @param {Array} slots [{ weekdays[], start_time, end_time }]
 * @param {Array} others affectations du remplaçant lues dans care
 * @returns {Array} les affectations en cause, sans doublon
 */
export const findOverlaps = (range, slots, others) => {
  return (others || []).filter((other) => {
    if (!OVERLAP_TYPES.includes(other.type)) {
      return false;
    }
    const otherEnd = day(other.end_date);
    const datesOverlap =
      (!otherEnd || otherEnd >= range.start_date) &&
      (!range.end_date || day(other.start_date) <= range.end_date);
    if (!datesOverlap) {
      return false;
    }
    return slots.some((slot) => {
      const sameDay = WEEKDAYS.some((weekday) => {
        return slot.weekdays.includes(weekday) && other[weekday] === true;
      });
      return (
        sameDay &&
        timesOverlap(
          slot.start_time,
          slot.end_time,
          other.start_time,
          other.end_time,
        )
      );
    });
  });
};

const checkCleaner = (piece, cleaner) => {
  const issues = [];
  if (!cleaner || cleaner.fullname === 'Affectation') {
    return issues;
  }
  if (has(cleaner, 'contract_start_date')) {
    const start = day(cleaner.contract_start_date);
    if (!start) {
      issues.push("Le remplaçant n'a pas de date de début de contrat.");
    } else if (piece.start_date < start) {
      issues.push(
        `Le morceau du ${formatDay(
          piece.start_date,
        )} commence avant le début du contrat du remplaçant (${formatDay(
          start,
        )}).`,
      );
    }
  }
  if (has(cleaner, 'contract_end_date')) {
    const end = day(cleaner.contract_end_date);
    if (end && piece.end_date > end) {
      issues.push(
        `Le morceau qui finit le ${formatDay(
          piece.end_date,
        )} dépasse la fin du contrat du remplaçant (${formatDay(
          end,
        )}) : renouvelez d'abord son CDD.`,
      );
    }
  }
  return issues;
};

const checkContract = (piece, contract) => {
  const issues = [];
  if (!contract) {
    return issues;
  }
  if (has(contract, 'effective_date')) {
    const start = day(contract.effective_date);
    if (!start) {
      issues.push("Le contrat client n'a pas de date de début.");
    } else if (piece.start_date < start) {
      issues.push(
        `Le morceau du ${formatDay(
          piece.start_date,
        )} commence avant le début du contrat client (${formatDay(start)}).`,
      );
    }
  }
  if (has(contract, 'end_date')) {
    const end = day(contract.end_date);
    if (end && piece.end_date > end) {
      issues.push(
        `Le morceau qui finit le ${formatDay(
          piece.end_date,
        )} dépasse la fin du contrat client (${formatDay(end)}).`,
      );
    }
  }
  return issues;
};

/**
 * @param {Array} pieces morceaux à créer { start_date, end_date }
 * @param {Object} data { cleaner, contract } lus dans care
 * @returns {string[]} motifs de refus prévisibles (dates de contrat)
 */
export const checkDates = (pieces, { cleaner, contract }) => {
  return pieces.reduce((issues, piece) => {
    return issues.concat(
      checkCleaner(piece, cleaner),
      checkContract(piece, contract),
    );
  }, []);
};
